import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  buildOfficePriceRows,
  OfficePriceRow,
} from 'src/components/Listings/InventoryActionDropdown/components/BulkChangeMarketplacePriceAction/officePriceRows.utils';
import { useAppContext } from 'src/contexts/AppContext';
import { useOffices } from 'src/hooks/api/useOffices';
import { BulkEditListingClient, ListingQuery } from 'src/WebApiController';

export const useBulkEditSelectionOffices = (
  filterQuery: ListingQuery | undefined
): {
  offices: OfficePriceRow[];
  isMultiOffice: boolean;
  isLoading: boolean;
  /** POS-9555: the selection's fee rows could not be loaded, so the dialog must not price. */
  hasSelectionError: boolean;
} => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { isOfficeEnabled, getOfficeName } = useOffices();

  // No feature flag required: gated on SellerAccount.IsOfficeEnabled (POS-9555)
  const enabled = isOfficeEnabled && !!filterQuery;

  const query = useQuery({
    queryKey: [
      'BulkEditListingClient.getBulkEditSelectionFeeRows',
      activeAccountWebClientConfig.activeAccountId,
      filterQuery,
    ],
    queryFn: () =>
      new BulkEditListingClient(
        activeAccountWebClientConfig
      ).getBulkEditSelectionFeeRows(filterQuery!),
    enabled,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  // POS-9555: on error we cannot tell which listings need the international fee, so falling
  // back to the account-level StubHub row would price international listings with the
  // domestic fee. The dialogs block submit instead of guessing.
  const hasSelectionError = enabled && query.isError;

  const offices = useMemo(
    () => buildOfficePriceRows(query.data, (id) => getOfficeName(id)),
    [query.data, getOfficeName]
  );

  return {
    offices,
    isMultiOffice: offices.length > 1,
    isLoading: enabled && query.isLoading,
    hasSelectionError,
  };
};
